// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import './Deliveries.scss';

import { useTranslation } from 'react-i18next';
import {
  collection,
  query,
  getDocs,
  doc,
  limit,
  orderBy,
  startAfter,
  where,
  serverTimestamp,
  onSnapshot,
  Timestamp,
  getFirestore,
  getDoc,
} from 'firebase/firestore';
import { firestore } from '../context/firebase';
import { useLocation, useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';

import DeliveryCard from '../components/DeliveryCard/DeliveryCard';
import Navbar from '../components/Navbar/Navbar';
import BaleFeedMonitor from '../components/BaleFeedMonitor/BaleFeedMonitor';

import logo_icon from '../assets/logos/upcircle_logo_circle.png';
import { DELIVERY_ROUTE } from '../constant/routes';
import { DELIVERIES_COLLECTION_PATH, DELIVERY_DOCUMENT_PATH } from '../shared_components/utils/FirebaseUtils';
import { getLabelsetBelongingToGroup } from '../shared_components/utils/FirebaseLabelsetsUtils';
import { DATE, FORMAT_VERSION } from '../constant/firebase';
import { useAuthStore } from '../stores/authStore';
import { useLocalStorage } from '@uidotdev/usehooks';
import { useSetDocument } from '../shared_components/hooks/useSetDocument';
import LoadingIcon from '../components/LoadingIcon/LoadingIcon';

export default function Deliveries() {
  const { t } = useTranslation();
  const location = useLocation();
  const currentUserClientId = useAuthStore((state) => state.currentUser?.company?.company);
  const currentUserDevices = useAuthStore((state) => state.currentUser?.devices);
  const baleFeedViewEnabled = useAuthStore((state) => state.deliverySettings?.features?.bale_feed_view);
  const logEventCustom = useAuthStore((state) => state.logEventCustom);
  const defaultLabelsets = useAuthStore((state) => state.defaultLabelsets);

  const [deliveries, setDeliveries] = React.useState({});
  const [showConveyorFeed, setShowConveyorFeed] = React.useState(true);
  const [smallScreenHeight, setSmallScreenHeight] = React.useState(false);
  const lastSnapshot = React.useRef(null);
  const allLoaded = React.useRef(false);
  const blockLoading = React.useRef(false);
  const scrollRef = React.useRef(null);
  const [deliveriesScrollStateTimeStamp, setDeliveriesScrollStateTimeStamp] = useLocalStorage(
    'deliveriesScrollStateTimeStamp',
    null,
  );
  const deliveryId = String(Math.floor(new Date().getTime() / 1000));
  const deliveryRef = doc(firestore, DELIVERY_DOCUMENT_PATH(currentUserClientId, deliveryId));
  const { setDocument: setDelivery, loading: isSetDeliveryLoading } = useSetDocument(deliveryRef);

  const LOAD_DOCS_BATCH_SIZE = 20;
  const RESET_SCROLL_POS_TIME_MINUTES = 30;

  const navigate = useNavigate();

  React.useEffect(() => {
    if (location.state?.deliveriesPageData?.deliveries) {
      const data = location.state?.deliveriesPageData?.deliveries;
      Object.entries(data).forEach(([k, v]) => {
        data[k] = { ...v, date: new Timestamp(v.date.seconds, v.date.nanoseconds) };
      });

      allLoaded.current = location.state?.deliveriesPageData?.allLoaded;
      setDeliveries(data);
    }
  }, [location]);

  React.useEffect(() => {
    subscribeToDeliveries();
  }, []);

  React.useLayoutEffect(() => {
    if (Object.values(deliveries ?? {}).length > 0 && location.state?.deliveriesPageData) {
      const currentDate = new Date();
      const scrollStateTimeStamp = new Date(deliveriesScrollStateTimeStamp);
      const diffMs = Math.abs(currentDate - scrollStateTimeStamp);
      const diffMinutes = diffMs / (1000 * 60);

      scrollRef.current?.scrollTo(
        0,
        (diffMinutes < RESET_SCROLL_POS_TIME_MINUTES && location.state?.deliveriesPageData?.positions) || 0,
      );
      const db = getFirestore();
      const path = location.state.deliveriesPageData.lastSnapshot;
      if (path) {
        const ref = doc(db, path);
        getDocAndStoreInLastSnapshot(ref);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [deliveries]);

  async function getDocAndStoreInLastSnapshot(docRef) {
    const snapshot = await getDoc(docRef);
    lastSnapshot.current = snapshot;
  }

  React.useEffect(() => {
    const checkScreenHeight = () => {
      setSmallScreenHeight(window.innerHeight < 600);
    };

    checkScreenHeight();
    window.addEventListener('resize', checkScreenHeight);

    return () => {
      window.removeEventListener('resize', checkScreenHeight);
    };
  }, []);

  function addDeliveriesFromSnapshot(newDeliveriesSnapshot) {
    /** Given a firebase snapshot containing delivery docs, add them to the complete deliveries dict */
    const newDeliveries = {};
    newDeliveriesSnapshot.forEach((doc) => {
      lastSnapshot.current = doc;
      newDeliveries[doc.id] = doc.data();
    });
    setDeliveries((prevDeliveries) => {
      return { ...prevDeliveries, ...newDeliveries };
    });

    // If no more documents are available, set the allLoaded flag
    if (newDeliveriesSnapshot.empty || newDeliveriesSnapshot.size < LOAD_DOCS_BATCH_SIZE) {
      allLoaded.current = true;
    }
  }

  function subscribeToDeliveries() {
    /** Subscribe to the deliveries collection and listen for changes. Only load newest batch */
    blockLoading.current = true;

    const deliveriesCollection = collection(firestore, DELIVERIES_COLLECTION_PATH(currentUserClientId));
    let deliveriesQuery = query(
      deliveriesCollection,
      where(FORMAT_VERSION, '==', 2),
      orderBy(DATE, 'desc'),
      limit(LOAD_DOCS_BATCH_SIZE),
    );

    onSnapshot(deliveriesQuery, (newDeliveriesSnapshot) => {
      addDeliveriesFromSnapshot(newDeliveriesSnapshot);
      blockLoading.current = false;
    });
  }

  async function paginateDeliveries() {
    /** Get all the deliveries from the user's company */
    if (allLoaded.current || blockLoading.current || lastSnapshot.current === null) {
      return;
    }

    blockLoading.current = true;

    const deliveriesCollection = collection(firestore, DELIVERIES_COLLECTION_PATH(currentUserClientId));
    let deliveriesQuery = query(
      deliveriesCollection,
      where(FORMAT_VERSION, '==', 2),
      orderBy(DATE, 'desc'),
      limit(LOAD_DOCS_BATCH_SIZE),
      startAfter(lastSnapshot.current),
    );

    const querySnapshot = await getDocs(deliveriesQuery);
    addDeliveriesFromSnapshot(querySnapshot);

    blockLoading.current = false;
  }

  const handleScroll = debounce((e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight < 200;
    if (bottom) {
      paginateDeliveries();
    }
  }, 200);

  async function createNewDelivery() {
    /** Create a new delivery */
    const defaultDelivery = {
      id: deliveryId,
      delivery_images: {},
      bales: {},
      date: serverTimestamp(),
      on_create_timestamp: serverTimestamp(),
      last_edit_timestamp: serverTimestamp(),
      notes: '',
      supplier_id: '',
      format_version: 2,
      delivery_input_completed: false,
      processing_status: 'completed',
      processed_by_interface: false,
    };

    const materialLabelsetId = getLabelsetBelongingToGroup(
      Object.keys(defaultLabelsets),
      defaultLabelsets,
      'materials',
    );

    const materialLabels = Object.keys(defaultLabelsets[materialLabelsetId]?.labels || {});
    const defaultMaterialLabelId = materialLabels?.[0];
    if (materialLabelsetId && defaultMaterialLabelId) {
      defaultDelivery['delivery_labels'] = { [materialLabelsetId]: defaultMaterialLabelId };
    }

    await setDelivery(defaultDelivery);

    logEventCustom('delivery_create', { delivery_id: deliveryId });

    navigate(DELIVERY_ROUTE, { state: { delivery_id: deliveryId, mode: 'delivery_overview' } });
  }

  return (
    <div className='deliveries'>
      <Navbar showConveyorFeed={showConveyorFeed} setShowConveyorFeed={setShowConveyorFeed} />
      {showConveyorFeed && !smallScreenHeight && currentUserDevices && baleFeedViewEnabled && (
        <div className='bale-feed-monitor-wrapper'>
          <h1 className='deliveries--title'>{t('deliveries_bale_feed_title')}</h1>
          <BaleFeedMonitor />
        </div>
      )}
      <h1 className='deliveries--title'>{t('deliveries_title')}</h1>
      <div className='deliveries--wrapper'>
        <div className='deliveries--cards-container' ref={scrollRef} onScroll={handleScroll}>
          <div className='deliveries--cards'>
            {Object.values(deliveries)
              .sort((a, b) => b?.date?.seconds - a?.date?.seconds)
              .map((delivery) => {
                return (
                  <DeliveryCard
                    key={delivery.id}
                    delivery={delivery}
                    lastSnapshot={lastSnapshot}
                    deliveriesPageData={{
                      deliveries,
                      scrollRef: scrollRef,
                      allLoaded: allLoaded,
                    }}
                    setDeliveriesScrollStateTimeStamp={setDeliveriesScrollStateTimeStamp}
                  />
                );
              })}
            {!allLoaded.current && (
              <div className='delivery_card'>
                <div className='delivery--img-container'>
                  <img src={logo_icon} className='spinning-logo' />
                </div>
                <p>{t('deliveries_loading')}...</p>
              </div>
            )}
          </div>
        </div>

        <div className='deliveries--button-wrapper'>
          <button className='deliveries--new-button' onClick={createNewDelivery}>
            {isSetDeliveryLoading ? <LoadingIcon /> : t('deliveries_new')}
          </button>
        </div>
      </div>
    </div>
  );
}
